/**
 * E-books Firestore Service
 * Handles CRUD operations for e-books collection
 */

import { database, storage } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp,
  increment,
  CollectionReference,
  DocumentReference,
  Query,
  DocumentData,
  QueryDocumentSnapshot,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import { EBook } from '@brovicms/models';
import { 
  EBookFormData, 
  EBookQueryOptions, 
  ServiceResult, 
  ListResult, 
  EBookStats 
} from '../types/admin';

class EBookService {
  private db: typeof database;
  private collectionName: string;
  private collection: CollectionReference<DocumentData>;

  constructor() {
    this.db = database;
    this.collectionName = 'ebooks';
    this.collection = collection(this.db, this.collectionName);
  }

  /**
   * Validate e-book data
   * @param ebookData - E-book data to validate
   * @param isUpdate - Whether this is an update operation
   * @returns Validation result
   */
  validateEBookData(ebookData: Partial<EBook>, isUpdate = false): { isValid: boolean; errors: string[] } {
    console.log('EBookService___validateEBookData', ebookData, isUpdate);
    const errors: string[] = [];

    // Required fields
    const requiredFields: (keyof EBookFormData)[] = ['title', 'author', 'description'];
    requiredFields.forEach(field => {
      const value = ebookData[field as keyof EBook];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(`${field} is required`);
      }
    });

    // Validate category separately since it's an object
    if (!ebookData.category || !ebookData.category.name) {
      errors.push('category is required');
    }

    // Title validation
    if (ebookData.title && ebookData.title.length < 3) {
      errors.push('Title must be at least 3 characters long');
    }

    if (ebookData.title && ebookData.title.length > 200) {
      errors.push('Title must be less than 200 characters');
    }

    // Author validation
    if (ebookData.author && ebookData.author.length < 2) {
      errors.push('Author name must be at least 2 characters long');
    }

    // Description validation
    if (ebookData.description && ebookData.description.length < 10) {
      errors.push('Description must be at least 10 characters long');
    }

    if (ebookData.description && ebookData.description.length > 2000) {
      errors.push('Description must be less than 2000 characters');
    }

    // // Price validation
    // if (ebookData.price !== undefined && ebookData.price !== null) {
    //   if (typeof ebookData.price !== 'number' || ebookData.price < 0) {
    //     errors.push('Price must be a positive number');
    //   }
    // }

    // Page count validation
    // if (ebookData.pageCount !== undefined && ebookData.pageCount !== null) {
    //   if (!Number.isInteger(ebookData.pageCount) || ebookData.pageCount < 1) {
    //     errors.push('Page count must be a positive integer');
    //   }
    // }

    // File validation (for new e-books)
    if (!isUpdate) {
      console.log('EBookService___validateEBookData:file', ebookData.file);
      if (!ebookData.file?.url && !ebookData.file?.fileName && !ebookData.file?.fileSize) {
        errors.push('PDF file is required');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Create a draft e-book document
   * @returns Draft document ID
   */
  async createDraft(): Promise<ServiceResult<{ docId: string }>> {
    try {
      const draftData = {
        isDraft: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      };

      const docRef = await addDoc(this.collection, draftData);
      
      return {
        success: true,
        id: docRef.id,
        data: { docId: docRef.id }
      };
    } catch (error: any) {
      console.error('Create draft error:', error);
      toast.error(`Failed to create draft: ${error.message}`);
      throw error;
    }
  }

  /**
   * Upload file to Firebase Storage
   * @param docId - Document ID
   * @param file - File to upload
   * @param type - Type of file (cover, ebook, index)
   * @param index - Index number for index images
   * @returns Download URL
   */
  async uploadFile(docId: string, file: File, type: 'cover' | 'ebook' | 'index', index?: number): Promise<string> {
    try {
      let filePath: string;
      
      switch (type) {
        case 'cover':
          filePath = `ebooks/${docId}/coverpage.png`;
          break;
        case 'ebook':
          filePath = `ebooks/${docId}/ebook_file.pdf`;
          break;
        case 'index':
          const extension = file.name.split('.').pop() || 'png';
          filePath = `ebooks/${docId}/index/${index}_${Date.now()}.${extension}`;
          break;
        default:
          throw new Error('Invalid file type');
      }

      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error: any) {
      console.error('Upload file error:', error);
      toast.error(`Failed to upload file: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update draft document with file URLs
   * @param docId - Document ID
   * @param updates - Updates to apply
   * @returns Update result
   */
  async updateDraft(docId: string, updates: Record<string, any>): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id: docId
      };
    } catch (error: any) {
      console.error('Update draft error:', error);
      toast.error(`Failed to update draft: ${error.message}`);
      throw error;
    }
  }

  /**
   * Publish draft (convert to published e-book)
   * @param docId - Document ID
   * @param ebookData - Complete e-book data
   * @returns Published e-book
   */
  async publishDraft(docId: string, ebookData: Partial<EBook>): Promise<ServiceResult<EBook>> {
    try {
      // Validate data
      const validation =this.validateEBookData(ebookData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const docRef = doc(this.db, this.collectionName, docId);
      
      // Prepare published data
      const publishedData = {
        ...ebookData,
        updatedBy: 'admin',
        isDraft: false,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'published',
        isPublished: true,
      };

      await updateDoc(docRef, publishedData);

      const result: ServiceResult<EBook> = {
        success: true,
        id: docId,
        data: { ...ebookData, id: docId } as EBook
      };

      toast.success('E-book published successfully');
      return result;

    } catch (error: any) {
      console.error('Publish draft error:', error);
      toast.error(`Failed to publish e-book: ${error.message}`);
      throw error;
    }
  }

  /**
   * Delete draft document
   * @param docId - Document ID
   * @returns Deletion result
   */
  async deleteDraft(docId: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await deleteDoc(docRef);

      // Also delete associated files from storage
      try {
        const coverRef = ref(storage, `ebooks/${docId}/coverpage.png`);
        await deleteObject(coverRef);
      } catch (error) {
        // File might not exist, ignore error
      }

      try {
        const ebookRef = ref(storage, `ebooks/${docId}/ebook_file.pdf`);
        await deleteObject(ebookRef);
      } catch (error) {
        // File might not exist, ignore error
      }

      // Delete index images
      try {
        const indexRef = ref(storage, `ebooks/${docId}/index/`);
        // Note: Firebase doesn't have a direct way to delete folders
        // Individual files would need to be deleted by name
      } catch (error) {
        // Ignore error
      }

      return {
        success: true,
        id: docId
      };
    } catch (error: any) {
      console.error('Delete draft error:', error);
      toast.error(`Failed to delete draft: ${error.message}`);
      throw error;
    }
  }

  /**
   * Delete only the draft document (do not delete any storage files)
   * Useful when user cancels draft but uploaded files should be retained
   */
  async deleteDraftDocOnly(docId: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await deleteDoc(docRef);
      return {
        success: true,
        id: docId
      };
    } catch (error: any) {
      console.error('Delete draft doc only error:', error);
      toast.error(`Failed to delete draft: ${error.message}`);
      throw error;
    }
  }

  /**
   * Create a new e-book
   * @param ebookData - E-book data
   * @returns Created e-book with ID
   */
  // async createEBook(ebookData: Partial<EBookFormData>): Promise<ServiceResult<EBook>> {
  //   try {
  //     // Validate data
  //     const validation = this.validateEBookData(ebookData);
  //     if (!validation.isValid) {
  //       throw new Error(validation.errors.join(', '));
  //     }

  //     // Prepare document data - map form data to EBook model
  //     const docData: Partial<EBook> = {
  //       id: '', // Will be set by Firestore
  //       title: ebookData.title?.trim() || '',
  //       author: ebookData.author?.trim() || '',
  //       description: ebookData.description?.trim() || '',
  //       category: { id: '', name: ebookData.category || '', slug: '', isActive: true, sortOrder: 0 }, // Map string to ContentCategory
  //       tags: ebookData.tags || [],
  //       language: ebookData.language || 'English',
  //       isPublished: ebookData.status === 'published',
  //       isFeatured: ebookData.featured || false,
  //       isPremium: !ebookData.isFree,
  //       createdAt: new Date(),
  //       updatedAt: new Date(),
  //       publishedAt: ebookData.status === 'published' ? new Date() : null,
  //       createdBy: ebookData.createdBy || 'admin',
  //       updatedBy: ebookData.createdBy || 'admin',
  //       metadata: {
  //         publishing: {
  //           publisher: ebookData.publisher || '',
  //           publishedDate: ebookData.publishedDate || null,
  //           pageCount: ebookData.pageCount || 0,
  //           price: ebookData.price || 0,
  //         }
  //       },
  //       stats: {
  //         views: 0,
  //         downloads: 0,
  //         shares: 0,
  //         likes: 0,
  //         bookmarks: 0,
  //         averageRating: 0,
  //         totalRatings: 0,
  //         comments: 0
  //       },
  //       type: 'ebook',
  //       file: {
  //         url: ebookData.pdfUrl || '',
  //         fileName: '',
  //         fileSize: 0,
  //         mimeType: 'application/pdf',
  //         pages: ebookData.pageCount || 0,
  //       },
  //       coverImage: {
  //         url: ebookData.coverImageUrl || '',
  //         alt: ebookData.title || '',
  //         width: 0,
  //         height: 0,
  //       },
  //       readingTime: Math.ceil((ebookData.pageCount || 0) * 2), // Estimate 2 minutes per page
  //       tableOfContents: [],
  //     };

  //     // Add document to Firestore
  //     const docRef = await addDoc(this.collection, docData);
      
  //     const result: ServiceResult<EBook> = {
  //       success: true,
  //       id: docRef.id,
  //       data: { ...docData, id: docRef.id } as EBook
  //     };

  //     toast.success('E-book created successfully');
  //     return result;

  //   } catch (error: any) {
  //     console.error('Create e-book error:', error);
  //     toast.error(`Failed to create e-book: ${error.message}`);
  //     throw error;
  //   }
  // }

  /**
   * Get e-book by ID
   * @param id - E-book ID
   * @returns E-book data
   */
  async getEBook(id: string): Promise<ServiceResult<EBook>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('E-book not found');
      }

      return {
        success: true,
        id: docSnap.id,
        data: { ...docSnap.data(), id: docSnap.id } as EBook
      };
    } catch (error: any) {
      console.error('Get e-book error:', error);
      toast.error(`Failed to get e-book: ${error.message}`);
      throw error;
    }
  }

  // /**
  //  * Update e-book
  //  * @param id - E-book ID
  //  * @param updateData - Data to update
  //  * @returns Update result
  //  */
  // async updateEBook(id: string, updateData: Partial<EBook>): Promise<ServiceResult<EBook>> {
  //   try {
  //     // Validate data
  //     const validation = this.validateEBookData(updateData, true);
  //     if (!validation.isValid) {
  //       throw new Error(validation.errors.join(', '));
  //     }

  //     const docRef = doc(this.db, this.collectionName, id);
      
  //     // Prepare update data
  //     const docData = {
  //       ...updateData,
  //       updatedAt: serverTimestamp(),
  //       updatedBy: updateData?.updatedBy || 'admin'
  //     };

  //     console.log('EBookService___updateEBook:docData', docData);
  //     // Clean up undefined values
  //     Object.keys(docData).forEach(key => {
  //       if (docData[key as keyof typeof docData] === undefined) {
  //         delete (docData as any)[key];
  //       }
  //     });

  //     console.log('EBookService___updateEBook:docData:after', docData);
  //     await updateDoc(docRef, docData);

  //     const result: ServiceResult<EBook> = {
  //       success: true,
  //       id,
  //       data: { ...docData, type: 'ebook' } as EBook
  //     };

  //     toast.success('E-book updated successfully');
  //     return result;

  //   } catch (error) {
  //     console.error('Update e-book error:', error);
  //     toast.error(`Failed to update e-book: ${error.message}`);
  //     throw error;
  //   }
  // }

  /**
   * Delete e-book
   * @param id - E-book ID
   * @returns Deletion result
   */
  async deleteEBook(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await deleteDoc(docRef);

      const result = {
        success: true,
        id,
        deletedAt: new Date().toISOString()
      };

      toast.success('E-book deleted successfully');
      return result;

    } catch (error) {
      console.error('Delete e-book error:', error);
      toast.error(`Failed to delete e-book: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  //write a function to delete draft ebooks
  async deleteDraftEBooks(): Promise<ServiceResult<void>> {
    try {
      const q = query(this.collection, where('isDraft', '==', true));
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(this.db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      await batch.commit();

      const result = {
        success: true,
        deletedAt: new Date().toISOString()
      };

      // toast.success('Draft e-books deleted successfully');
      return result;
    }
    catch (error) {
      console.error('Delete draft ebooks error:', error);
      toast.error(`Failed to delete draft ebooks: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }
  /**
   * Get all e-books with pagination and filtering
   * @param options - Query options
   * @returns E-books list with pagination info
   */
  async getEBooks(options: EBookQueryOptions = {}): Promise<ListResult<EBook>> {
    try {
      const {
        limit: limitCount = undefined,
        orderBy: orderByField = 'createdAt',
        orderDirection = 'desc',
        status = null,
        isPublished = true,
        category = null,
        featured = null,
        search = null,
        isDraft = false,
        startAfterDoc = null
      } = options;

      let q = query(this.collection);

      // Apply filters
      if (status) {
        q = query(q, where('status', '==', status));
      }

      // Filter by published flag if explicitly provided
      if (typeof isPublished === 'boolean') {
        q = query(q, where('isPublished', '==', isPublished));
      }

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (featured !== null) {
        q = query(q, where('featured', '==', featured));
      }

      // Apply ordering
      q = query(q, orderBy(orderByField, orderDirection));

      // Apply pagination
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }

      const querySnapshot = await getDocs(q);
      const ebooks: EBook[] = [];
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;

      querySnapshot.forEach((doc) => {
        ebooks.push({
          id: doc.id,
          ...doc.data()
        } as EBook);
        lastDoc = doc;
      });

      return {
        success: true,
        data: ebooks,
        error: null,
        pagination: {
          hasMore: ebooks.length === limitCount,
          lastDoc,
          total: ebooks.length
        }
      };

    } catch (error) {
      console.error('Get e-books error:', error);
      toast.error(`Failed to get e-books: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  // /**
  //  * Search e-books
  //  * @param searchTerm - Search term
  //  * @param options - Search options
  //  * @returns Search results
  //  */
  // async searchEBooks(searchTerm: string, options: { limit?: number } = {}): Promise<ListResult<EBook>> {
  //   try {
  //     const { limit: limitCount = 20 } = options;
      
  //     // Note: Firestore doesn't support full-text search natively
  //     // This is a basic implementation - consider using Algolia for better search
  //     const q = query(
  //       this.collection,
  //       where('status', '==', 'published'),
  //       orderBy('title'),
  //       limit(limitCount)
  //     );

  //     const querySnapshot = await getDocs(q);
  //     const allEbooks: EBook[] = [];
      
  //     querySnapshot.forEach((doc) => {
  //       allEbooks.push({
  //         id: doc.id,
  //         ...doc.data()
  //       } as EBook);
  //     });

  //     // Client-side filtering (not ideal for large datasets)
  //     const filteredEbooks = allEbooks.filter(ebook => {
  //       const searchLower = searchTerm.toLowerCase();
  //       return (
  //         ebook.title.toLowerCase().includes(searchLower) ||
  //         ebook.author.toLowerCase().includes(searchLower) ||
  //         ebook.description.toLowerCase().includes(searchLower) ||
  //         ebook.tags.some((tag: string) => tag.toLowerCase().includes(searchLower))
  //       );
  //     });

  //     return {
  //       success: true,
  //       data: filteredEbooks,
  //       searchTerm,
  //       total: filteredEbooks.length
  //     };

  //   } catch (error) {
  //     console.error('Search e-books error:', error);
  //     toast.error(`Search failed: ${error.message}`);
  //     throw error;
  //   }
  // }

  /**
   * Increment download count
   * @param id - E-book ID
   * @returns Update result
   */
  async incrementDownloadCount(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await updateDoc(docRef, {
        downloadCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id
      };
    } catch (error) {
      console.error('Increment download count error:', error);
      throw error;
    }
  }

  /**
   * Increment view count
   * @param id - E-book ID
   * @returns Update result
   */
  async incrementViewCount(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await updateDoc(docRef, {
        viewCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id
      };
    } catch (error) {
      console.error('Increment view count error:', error);
      throw error;
    }
  }

  // /**
  //  * Update e-book rating
  //  * @param id - E-book ID
  //  * @param rating - New rating (1-5)
  //  * @returns Update result
  //  */
  // async updateRating(id: string, rating: number): Promise<ServiceResult<{ newRating: number; newCount: number }>> {
  //   try {
  //     if (rating < 1 || rating > 5) {
  //       throw new Error('Rating must be between 1 and 5');
  //     }

  //     const docRef = doc(this.db, this.collectionName, id);
  //     const docSnap = await getDoc(docRef);
      
  //     if (!docSnap.exists()) {
  //       throw new Error('E-book not found');
  //     }

  //     const currentData = docSnap.data();
  //     const currentRating = currentData.rating || 0;
  //     const currentCount = currentData.ratingCount || 0;
      
  //     const newCount = currentCount + 1;
  //     const newRating = ((currentRating * currentCount) + rating) / newCount;

  //     await updateDoc(docRef, {
  //       rating: Math.round(newRating * 10) / 10, // Round to 1 decimal
  //       ratingCount: newCount,
  //       updatedAt: serverTimestamp()
  //     });

  //     return {
  //       success: true,
  //       id,
  //       newRating,
  //       newCount
  //     };
  //   } catch (error) {
  //     console.error('Update rating error:', error);
  //     throw error;
  //   }
  // }

  // /**
  //  * Get e-book statistics
  //  * @returns Statistics data
  //  */
  // async getEBookStats(): Promise<ServiceResult<EBookStats>> {
  //   try {
  //     const q = query(this.collection);
  //     const querySnapshot = await getDocs(q);
      
  //     let totalEbooks = 0;
  //     let publishedEbooks = 0;
  //     let draftEbooks = 0;
  //     let totalDownloads = 0;
  //     let totalViews = 0;
  //     const categories: Record<string, number> = {};

  //     querySnapshot.forEach((doc) => {
  //       const data = doc.data() as any;
  //       totalEbooks++;
        
  //       if (data.isPublished === true) publishedEbooks++;
  //       if (data.isPublished === false) draftEbooks++;
        
  //       totalDownloads += data.stats?.downloads || 0;
  //       totalViews += data.stats?.views || 0;
  //       const category = data.category?.name || 'Uncategorized';
  //       categories[category] = (categories[category] || 0) + 1;
  //     });

  //     return {
  //       success: true,
  //       stats: {
  //         totalEbooks,
  //         publishedEbooks,
  //         draftEbooks,
  //         totalDownloads,
  //         totalViews,
  //         categories
  //       }
  //     };
  //   } catch (error) {
  //     console.error('Get e-book stats error:', error);
  //     throw error;
  //   }
  // }
}

// Create and export singleton instance
const ebookService = new EBookService();
export default ebookService;
